import { instant, number, result } from './birthCalculations';
export interface ScreeningPolicy<A extends string> {
  enabled: boolean;
  clinically_reviewed: boolean;
  citation: string;
  algorithm: A;
}
export interface NewbornScreeningConfiguration {
  cchd: ScreeningPolicy<'AAP_2025'>;
  ballard: ScreeningPolicy<'NEW_BALLARD_1991'>;
}
export interface CchdAttempt {
  id: string;
  subject_id: string;
  at: string;
  right_hand_pct: number;
  foot_pct: number;
  room_air: boolean;
  early_discharge: boolean;
}
export const ballardComponentRanges: Record<string, readonly [number, number]> = {
  posture: [0, 4],
  square_window: [-1, 4],
  arm_recoil: [0, 4],
  popliteal_angle: [-1, 5],
  scarf_sign: [-1, 4],
  heel_to_ear: [-1, 4],
  skin: [-1, 5],
  lanugo: [-1, 4],
  plantar_surface: [-2, 4],
  breast: [-1, 4],
  eye_ear: [-2, 4],
  genitals: [-1, 4],
};
export function validateNewbornScreeningConfiguration(value: unknown): string[] {
  if (typeof value !== 'object' || value === null || Array.isArray(value))
    return ['Invalid newborn screening configuration.'];
  const v = value as Record<string, unknown>;
  if (Object.keys(v).sort().join(',') !== 'ballard,cchd') return ['Both supported screening policies required.'];
  const errors: string[] = [];
  for (const [key, algorithm] of [
    ['cchd', 'AAP_2025'],
    ['ballard', 'NEW_BALLARD_1991'],
  ]) {
    const p = v[key];
    if (typeof p !== 'object' || p === null || Array.isArray(p)) {
      errors.push(key + ': invalid policy.');
      continue;
    }
    const policy = p as Record<string, unknown>;
    if (
      Object.keys(policy).sort().join(',') !== 'algorithm,citation,clinically_reviewed,enabled' ||
      policy.algorithm !== algorithm ||
      typeof policy.enabled !== 'boolean' ||
      typeof policy.clinically_reviewed !== 'boolean' ||
      typeof policy.citation !== 'string' ||
      !policy.citation.trim() ||
      policy.citation.length > 2000 ||
      (policy.enabled && !policy.clinically_reviewed)
    )
      errors.push(key + ': reviewed supported algorithm and citation required.');
  }
  return errors;
}
const enabled = (p: ScreeningPolicy<string> | undefined, algorithm: string) =>
  p?.enabled === true && p.clinically_reviewed === true && p.algorithm === algorithm && !!p.citation.trim();
export function cchdScreen(
  attempts: CchdAttempt[],
  subjectId: string,
  birthAt: string,
  policy: ScreeningPolicy<'AAP_2025'> | undefined,
) {
  type Value = { status: 'PASS' | 'FAIL' | 'REPEAT' | 'NOT_READY'; repeat_at_ms?: number };
  const ids: string[] = [];
  const invalid = (message: string) => result<Value | null>(null, ids, [message]);
  if (!enabled(policy, 'AAP_2025')) return invalid('Reviewed AAP 2025 screening policy required.');
  const birth = instant(birthAt);
  if (birth === null) return invalid('Birth time with timezone required.');
  const own = attempts.filter((a) => a.subject_id === subjectId);
  if (!own.length) return invalid('No documented screening attempts.');
  if (new Set(own.map((a) => a.id)).size !== own.length) return invalid('Duplicate screening attempt identity.');
  let previous: number | null = null;
  for (const a of [...own].sort((a, b) => (instant(a.at) ?? Infinity) - (instant(b.at) ?? Infinity))) {
    ids.push(a.id);
    const at = instant(a.at);
    if (
      at === null ||
      at < birth ||
      ![a.right_hand_pct, a.foot_pct].every((n) => number(n) && n >= 0 && n <= 100) ||
      typeof a.early_discharge !== 'boolean' ||
      a.room_air !== true
    )
      return invalid('Valid paired percentages, explicit room-air status and chronology required.');
    const low = Math.min(a.right_hand_pct, a.foot_pct),
      difference = Math.abs(a.right_hand_pct - a.foot_pct);
    if (low < 90)
      return result<Value | null>({ status: 'FAIL' }, ids, [
        'Failed screen is not a diagnosis; clinician evaluation required.',
      ]);
    if ((at - birth) / 3600000 < 24 && !a.early_discharge)
      return result<Value | null>({ status: 'NOT_READY' }, ids, [
        'Before 24 hours: explicit early-discharge context required.',
      ]);
    if (previous !== null && at - previous < 3600000)
      return invalid('Repeat must be separated by at least one hour; do not classify an early repeat.');
    if (low >= 95 && difference <= 3)
      return result<Value | null>({ status: 'PASS' }, ids, [
        'Passing pulse oximetry does not exclude all congenital heart defects.',
      ]);
    if (previous !== null)
      return result<Value | null>({ status: 'FAIL' }, ids, [
        'Failed screen after one properly timed repeat; clinician evaluation required.',
      ]);
    previous = at;
  }
  return result<Value | null>({ status: 'REPEAT', repeat_at_ms: previous! + 3600000 }, ids, [
    'Repeat screening per the reviewed protocol.',
  ]);
}
export function ballardScore(
  components: Record<string, unknown>,
  examAt: string,
  birthAt: string,
  policy: ScreeningPolicy<'NEW_BALLARD_1991'> | undefined,
) {
  type Value = { total: number; ga_weeks: number };
  const inputs = { components, exam_at: examAt, birth_at: birthAt };
  const invalid = (message: string) => result<Value | null>(null, inputs, [message]);
  if (!enabled(policy, 'NEW_BALLARD_1991')) return invalid('Reviewed New Ballard policy required.');
  if (
    Object.keys(components).sort().join(',') !== Object.keys(ballardComponentRanges).sort().join(',') ||
    Object.entries(ballardComponentRanges).some(
      ([key, [low, high]]) =>
        !number(components[key]) ||
        !Number.isInteger(components[key]) ||
        components[key] < low ||
        components[key] > high,
    )
  )
    return invalid('All 12 original scored components required within their supported ranges.');
  const at = instant(examAt),
    birth = instant(birthAt);
  if (at === null || birth === null || at < birth || (at - birth) / 3600000 > 96)
    return invalid('Assessment must have valid chronological timestamps within 96 hours of birth.');
  const total = Object.values(components).reduce<number>((sum, n) => sum + (n as number), 0);
  if (total < -10 || total > 50) return invalid('Total lies outside the validated 20–44 week conversion table.');
  const ga = 24 + 0.4 * total;
  return result<Value | null>({ total, ga_weeks: ga }, inputs, [
    ...(ga < 26 && (at - birth) / 3600000 >= 12
      ? ['For assessments below 26 weeks, the source reports greatest validity before 12 hours.']
      : []),
    'Numeric scored-component calculation only. Use the authorized assessment tool; no pregnancy dating change.',
  ]);
}
